import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { useEnvironment } from '@ori/environment';
import { useSWR } from '@ori/fetching';
import { TranslationsNamespaceProvider, TranslationsProvider } from '@ori/i18n';
import { useDisclose } from '@ori/presentation-hooks';
import { Team, TestIdProvider } from '@ori/testid-generator';
import type { FC } from 'react';
import { useEffect } from 'react';

import type { DefaultTranslations } from '../../constants';
import { PACKAGE_NAME, defaultTranslations, translationsNamespace } from '../../constants';
import { getCountryCode, getGeolocationData } from '../../utils';
import { GeolocationDialog } from '../GeolocationDialog';

export interface GeolocationProps {
  /** When enabled is true other conditions are checked whether geolocation popup can be visible. */
  enabled: boolean;
  /** If translations are not provided they are fetched on client. */
  translations?: DefaultTranslations;
}

/**
 * Geolocation component that fetches data needed to decide visibility of geolocatin popup. If conditions are met a dialog with information about home and foreign country is displayed.
 */
export const Geolocation: FC<GeolocationProps> = ({ enabled, translations }) => {
  const { staticApiUrl } = useServiceUrls(['staticApi']);
  const { environment, tenant } = useEnvironment();
  const { isAnonymousUser } = useToken();
  const { open, onClose, onOpen } = useDisclose();

  const {
    data: { foreignCountry, homeCountry, homeCountryUrl, visible } = {
      foreignCountry: '',
      homeCountry: '',
      homeCountryUrl: '',
      visible: false,
    },
  } = useSWR(
    [enabled, environment, isAnonymousUser, staticApiUrl, tenant],
    async () => getGeolocationData({ enabled, environment, isAnonymousUser, staticApiUrl, tenant }),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  useEffect(() => {
    if (visible) {
      onOpen();
    }
  }, [onOpen, visible]);

  // We need to fetch translations with home country tenant to get correct translations.
  const countryCode = getCountryCode() ?? 'uk';

  if (!open) {
    return null;
  }

  return (
    <TestIdProvider
      team={Team.Presentation}
      project={PACKAGE_NAME}
    >
      <TranslationsProvider
        defaultTranslationsWithNamespaces={{ [translationsNamespace]: defaultTranslations }}
        // eslint-disable-next-line no-console -- we want to log errors
        clientFetchConfiguration={{ onError: console.error, tenant: countryCode, baseUrl: staticApiUrl }}
        language={navigator.language}
        // istanbul ignore next -- We do not need to test fetching of translations when translations are undefined.
        translationsWithNamespaces={translations ? { [translationsNamespace]: translations } : undefined}
      >
        <TranslationsNamespaceProvider namespace={translationsNamespace}>
          <GeolocationDialog
            foreignCountry={foreignCountry}
            homeCountry={homeCountry}
            homeCountryUrl={homeCountryUrl}
            open={open}
            onClose={onClose}
          />
        </TranslationsNamespaceProvider>
      </TranslationsProvider>
    </TestIdProvider>
  );
};
